import { useCallback, useEffect, useState } from "react";
import CommonModal from "../../components/comman/modal/Modal";
import OfferingForm from "../../components/chatbots/Chatbot";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchChatbotOfferings, addOffering, updateOffering, deleteOffering } from "../../redux/actions/chatbotActions/offering.actions";
import ViewChatbotOffering from "../../components/chatbots/Chatbot/ViewChatbotOffering";
import DeleteChatbotOffering from "../../components/chatbots/Chatbot/DeleteChatbotOffering";
import CardSection from "./CardSection";
import ChatbotTablePagination from "../../components/comman/ChatBotTable/WithPagination";
import { debounce } from "lodash";
import { motion } from "framer-motion";
import "./chatbotManagement.scss";

const ChatbotManagement = () => {
  const dispatch = useDispatch();
  const { offerings, categories, countries, categoryTotal, offeringTotal, offeringPage, cardData } = useSelector(state => ({
    offerings: state?.chatBotReducers?.chatbotOfferings?.data || [],
    cardData: state.cardReducer?.cards?.data || [],
    categories: state.categoryReducer?.categories?.data || [],
    countries: state.countryReducer?.countries?.data || [],
    categoryTotal: state.chatBotReducers?.chatbotOfferings?.meta?.totalPages || 0,
    offeringTotal: state.chatBotReducers?.chatbotOfferings?.meta?.total || 0,
    offeringPage: state.chatBotReducers?.chatbotOfferings?.meta?.page || 1
  }), shallowEqual);



  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add"); // add | edit | view
  const [selectedOffering, setSelectedOffering] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [reset, setReset] = useState(false);


  const handleAdd = () => {
    setSelectedOffering(null);
    setModalType("add");
    setModalOpen(true);
  };

  const handleEdit = (offering) => {
    setSelectedOffering(offering);
    setModalType("edit");
    setModalOpen(true);
  };

  const handleDelete = (offering) => {
    setSelectedOffering(offering);
    setModalType("delete");
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedOffering) {
      dispatch(deleteOffering(selectedOffering.id));
    }
    setModalOpen(false);
  };

  const handleFormSubmit = async (formData) => {
    let payload = null;
    if (modalType === "edit") {
      const { countries, brand_name, categories, ...newData } = formData;
      payload = {
        ...newData,
        brand_id: Number(formData?.brand_id)
      }
      dispatch(updateOffering(selectedOffering.id, payload));
      setModalOpen(false);
    } else {
      payload = {
        ...formData,
        brand_id: Number(formData?.brand_id)
      }
      const response = await dispatch(addOffering(payload));
      setModalOpen(false);
    }
  };

  const columns = [
    { key: "id", label: "ID" },
    { key: "offer", label: "Offer" },
    { key: "brand", label: "Brand" },
    { key: "preference", label: "Preference" },
    { key: "categories", label: "Categories" },
    { key: "countries", label: "Countries" },
    { key: "priority", label: "Priority" },
    { key: "creative_media", label: "Creative Media" },
    { key: "status", label: "Status" }

  ];

  const tableData = offerings.map((offering) => ({
    ...offering,
    categories: offering.category_ids
      .map((id) => categories.find((c) => c.id === id)?.category)
      .filter(Boolean)
      .join(", "),
    countries: offering.country_ids
      .map((id) => countries.find((c) => c.id === id)?.name)
      .filter(Boolean)
      .join(", "),
  }));

  const handleStatusClick = (offering) => {
    dispatch(updateOffering(offering.id, { status: offering.status === 1 ? 0 : 1 }));
  };

  const handleClosePopUp = () => {
    setModalOpen(false);
    setReset(true);
  }

  const handlePagination = (page) => {
    dispatch(fetchChatbotOfferings(false, page, 5, ""));
  };

  //search
  const debouncedSearch = useCallback(debounce((searchTerm) => dispatch(fetchChatbotOfferings(false, 1, 5, searchTerm)), 500), []);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);




  return (
    <div className="content-chatbot chatbot-management">
      <motion.div
        className="chatbot-management-intro"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <h2>Chatbot Management Overview</h2>
        <p>
          Manage chatbot offerings, update rules, and monitor mapped categories and countries in one place.
        </p>
      </motion.div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <CardSection />
      </div>

      {/* Offerings Table */}
      <ChatbotTablePagination
        title="Offerings"
        columns={columns}
        data={tableData}
        btnName="Add New Offering"
        onAdd={handleAdd}
        onView={(row) => {
          setSelectedOffering(row);
          setModalType("view");
          setModalOpen(true);
        }}
        total={offeringTotal}
        page={offeringPage}
        limit={5}
        totalPages={Math.ceil(offeringTotal / 5)}
        rowsPerPage={5}
        onEdit={handleEdit}
        onDelete={handleDelete}
        handleStatusClick={handleStatusClick}
        categoryTotal={categoryTotal}
        handlePagination={handlePagination}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Add/Edit/View Modal */}
      {modalOpen && (
        <CommonModal
          isOpen={modalOpen}
          onClose={handleClosePopUp}
          title={
            modalType === "add"
              ? "Add New Offering"
              : modalType === "edit"
                ? "Edit Offering"
                : "Chatbot Offering Details"
          }
          widthHeight="900px"
        >
          {
            modalType === "view" ? (
              <ViewChatbotOffering
                chatbotOffering={selectedOffering}
                categories={categories}
                countries={countries}
                tiers={cardData}
              />
            ) :
              modalType === "add" || modalType === "edit" ? (
                <OfferingForm
                  onSubmit={handleFormSubmit}
                  updateData={modalType !== "add" ? selectedOffering : null}
                  resetForm={reset}
                />
              ) : null
          }
        </CommonModal>
      )}

      {
        modalType === "delete" &&
        <CommonModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Confirm Delete"
          widthHeight="600px"
        >
          <DeleteChatbotOffering
            onClose={() => setModalOpen(false)}
            onDelete={handleConfirmDelete}
            chatbotOffering={selectedOffering}
          />
        </CommonModal>
      }
    </div>
  );
};

export default ChatbotManagement;
